/**
 * Why a google-location request failed, in the terms the panel speaks.
 *
 * Read from the HTTP status rather than the code in the body's `error`: the
 * codes (INVALID_INPUT, PLACE_NOT_FOUND, PLACE_NOT_PICKED, RATE_LIMITED,
 * MAPS_UNAVAILABLE) map one to one onto 400, 404, 409, 429 and 503, and a
 * status survives a proxy or a gateway error page that the body would not.
 */
export type RequestFailure =
  | { kind: 'rate-limited'; retryAfterSeconds: number | null }
  | { kind: 'unavailable' }
  | { kind: 'not-found' }
  /** A save of a place no search picked within the day it is remembered. */
  | { kind: 'not-picked' }
  | { kind: 'invalid' }
  | { kind: 'unknown' }

interface FailedResponse {
  status?: number
  data?: { retryAfter?: unknown } | null
  headers?: Record<string, unknown>
}

/** Seconds, from either a number or a `Retry-After` header's text. */
const toSeconds = (value: unknown, now: number): number | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return Math.ceil(value)
  }
  if (typeof value !== 'string' || value.trim() === '') return null
  const seconds = Number(value)
  if (Number.isFinite(seconds) && seconds >= 0) return Math.ceil(seconds)
  // The header may also be a date.
  const date = Date.parse(value)
  return Number.isNaN(date) ? null : Math.max(Math.ceil((date - now) / 1000), 0)
}

export function toRequestFailure(
  error: unknown,
  now = Date.now()
): RequestFailure {
  const response = (error as { response?: FailedResponse } | null)?.response
  switch (response?.status) {
    case 429:
      return {
        kind: 'rate-limited',
        retryAfterSeconds:
          toSeconds(response.data?.retryAfter, now) ??
          toSeconds(response.headers?.['retry-after'], now)
      }
    case 503:
      return { kind: 'unavailable' }
    case 404:
      return { kind: 'not-found' }
    case 409:
      return { kind: 'not-picked' }
    case 400:
      return { kind: 'invalid' }
    default:
      return { kind: 'unknown' }
  }
}

/**
 * The rate-limit notice for a wait of `seconds`, in the unit it is counted in.
 * The backend's windows are a minute, an hour and a day, and "3540 s" is a sum
 * for the owner to do. Null or used up says only that it is soon.
 */
export function rateLimitMessage(seconds: number | null): {
  key: string
  values?: Record<string, number>
} {
  if (seconds === null || seconds <= 0) {
    return { key: 'GOOGLE_LOCATION.SEARCH.RATE_LIMITED_SOON' }
  }
  if (seconds < 60) {
    return { key: 'GOOGLE_LOCATION.SEARCH.RATE_LIMITED', values: { seconds } }
  }
  if (seconds < 3600) {
    return {
      key: 'GOOGLE_LOCATION.SEARCH.RATE_LIMITED_MINUTES',
      values: { minutes: Math.ceil(seconds / 60) }
    }
  }
  return {
    key: 'GOOGLE_LOCATION.SEARCH.RATE_LIMITED_HOURS',
    values: { hours: Math.ceil(seconds / 3600) }
  }
}
